import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import type { PromoConfig } from '@/types/promo';
import { fetchRuntimeActivePromo } from '@/services/promo/promoApi';

const PROMO_REFRESH_INTERVAL_MS = 60_000;

interface PromoContextValue {
  promo: PromoConfig | null;
  isPromoActive: boolean;
  loading: boolean;
  refreshPromo: () => Promise<void>;
}

const PromoContext = createContext<PromoContextValue | undefined>(undefined);

export const PromoProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [promo, setPromo] = useState<PromoConfig | null>(null);
  const [loading, setLoading] = useState(true);

  const refreshPromo = useCallback(async () => {
    try {
      const nextPromo = await fetchRuntimeActivePromo();
      setPromo(nextPromo);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let isMounted = true;

    void (async () => {
      const nextPromo = await fetchRuntimeActivePromo();
      if (isMounted) {
        setPromo(nextPromo);
        setLoading(false);
      }
    })();

    const intervalId = window.setInterval(() => {
      void refreshPromo();
    }, PROMO_REFRESH_INTERVAL_MS);

    return () => {
      isMounted = false;
      window.clearInterval(intervalId);
    };
  }, [refreshPromo]);

  const value = useMemo<PromoContextValue>(
    () => ({
      promo,
      isPromoActive: promo !== null,
      loading,
      refreshPromo,
    }),
    [promo, loading, refreshPromo],
  );

  return <PromoContext.Provider value={value}>{children}</PromoContext.Provider>;
};

export function usePromoContext(): PromoContextValue {
  const ctx = useContext(PromoContext);
  if (!ctx) {
    throw new Error('usePromoContext must be used within PromoProvider');
  }
  return ctx;
}
